import type { ReactNode, RefObject } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

export function AnchoredMenu({
  open,
  onOpenChange,
  anchorRef,
  children,
  className,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchorRef: RefObject<HTMLElement | null>;
  children: ReactNode;
  className?: string;
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | undefined>(undefined);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(undefined);
      return;
    }

    function updatePosition(): void {
      const anchor = anchorRef.current;
      const menu = menuRef.current;
      if (!anchor || !menu) {
        return;
      }

      const rect = anchor.getBoundingClientRect();
      const margin = 8;
      const gap = 4;
      const width = menu.offsetWidth;
      const height = menu.offsetHeight;

      let left = rect.right - width;
      left = Math.min(Math.max(margin, left), Math.max(margin, window.innerWidth - width - margin));

      const belowTop = rect.bottom + gap;
      const aboveTop = rect.top - height - gap;
      const fitsBelow = belowTop + height <= window.innerHeight - margin;
      const top = fitsBelow ? belowTop : Math.max(margin, aboveTop);

      setPosition({ left, top });
    }

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function handlePointerDown(event: PointerEvent): void {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || anchorRef.current?.contains(target)) {
        return;
      }

      onOpenChange(false);
    }

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        onOpenChange(false);
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onOpenChange, anchorRef]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      ref={menuRef}
      className={cn(
        "fixed z-50 max-h-[min(16rem,calc(100vh-16px))] overflow-y-auto rounded-md border border-white/10 bg-[#242a31] p-1 shadow-xl shadow-black/30",
        className,
      )}
      style={position ? { left: position.left, top: position.top } : { left: 0, top: 0, visibility: "hidden" }}
    >
      {children}
    </div>,
    document.body,
  );
}
